import { cn } from "@/lib/utils";

type StageState = "completed" | "active" | "overdue" | "upcoming";

const PRIMARY_STAGES = [
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "partial", label: "Partial" },
  { key: "paid", label: "Paid" },
] as const;

const ALT_TERMINAL = { key: "cancelled", label: "Cancelled" } as const;

type PrimaryStage = (typeof PRIMARY_STAGES)[number]["key"];
type AltTerminal = typeof ALT_TERMINAL.key;
type Overdue = "overdue";
type AnyStage = PrimaryStage | AltTerminal | Overdue;

interface InvoiceStageTrackerProps {
  /**
   * The current invoice stage. `null`/undefined falls back to `draft`.
   * `refunded` collapses onto `paid` as a successful terminal.
   * `overdue` colours the anchor primary node (Sent) danger and leaves later stages upcoming.
   * Unrecognized values render as if `draft`.
   */
  stage: AnyStage | "refunded" | string | null | undefined;
  /** Optional className for the outer wrapper. */
  className?: string;
}

function normalizeStage(stage: InvoiceStageTrackerProps["stage"]): AnyStage {
  if (!stage) return "draft";
  if (stage === "refunded") return "paid";
  const known: AnyStage[] = [
    "draft",
    "sent",
    "partial",
    "paid",
    "overdue",
    "cancelled",
  ];
  return (known as readonly string[]).includes(stage)
    ? (stage as AnyStage)
    : "draft";
}

// The primary node the danger marker anchors on when status === "overdue".
// Today getInvoice does not expose a "last reached" stage, so we default to
// the "Sent" node. Future enhancement: a `lastReachedStage` prop.
const OVERDUE_ANCHOR: PrimaryStage = "sent";

function primaryStageState(
  stageKey: PrimaryStage,
  current: AnyStage,
): StageState {
  if (current === "cancelled") return "upcoming";
  if (current === "overdue") {
    const anchorIdx = PRIMARY_STAGES.findIndex((s) => s.key === OVERDUE_ANCHOR);
    const idx = PRIMARY_STAGES.findIndex((s) => s.key === stageKey);
    if (idx < anchorIdx) return "completed";
    if (idx === anchorIdx) return "overdue";
    return "upcoming";
  }
  const idx = PRIMARY_STAGES.findIndex((s) => s.key === stageKey);
  const cur = PRIMARY_STAGES.findIndex((s) => s.key === current);
  if (idx < cur) return "completed";
  if (idx === cur) return "active";
  return "upcoming";
}

function altTerminalState(current: AnyStage): StageState {
  return current === "cancelled" ? "active" : "upcoming";
}

interface NodeProps {
  label: string;
  state: StageState;
}

function StageNode({ label, state }: NodeProps) {
  const diamondColor =
    state === "active"
      ? "var(--color-warning)"
      : state === "completed"
        ? "var(--color-success)"
        : state === "overdue"
          ? "var(--color-danger)"
          : "var(--color-border)";

  return (
    <div data-state={state} className="flex flex-col items-center gap-1.5">
      <div
        className="h-2.5 w-2.5 rotate-45"
        style={{
          backgroundColor: state !== "upcoming" ? diamondColor : "transparent",
          border: `1.5px solid ${diamondColor}`,
        }}
        aria-hidden="true"
      />
      <span
        className={
          state === "active"
            ? "typo-label font-semibold text-[color:var(--color-text-hi)]"
            : state === "overdue"
              ? "typo-label font-semibold text-[color:var(--color-danger)]"
              : state === "completed"
                ? "typo-label text-[color:var(--color-text-md)]"
                : "typo-label text-[color:var(--color-text-lo)]"
        }
      >
        {label}
      </span>
    </div>
  );
}

interface ConnectorProps {
  /** Whether the path BEFORE this connector has been traversed (success colored). */
  passed: boolean;
}

function Connector({ passed }: ConnectorProps) {
  return (
    <div
      className="h-px flex-1 mt-[5px]"
      style={{
        backgroundColor: passed
          ? "var(--color-success)"
          : "var(--color-border)",
      }}
      aria-hidden="true"
    />
  );
}

export function InvoiceStageTracker({
  stage,
  className,
}: InvoiceStageTrackerProps) {
  const current = normalizeStage(stage);

  return (
    <div className={cn("flex items-start gap-2 w-full", className)}>
      {PRIMARY_STAGES.map((s, i) => {
        const state = primaryStageState(s.key, current);
        const nextState = PRIMARY_STAGES[i + 1]
          ? primaryStageState(PRIMARY_STAGES[i + 1].key, current)
          : "upcoming";
        const passed = state === "completed" && nextState !== "upcoming";
        return (
          <div key={s.key} className="contents">
            <StageNode label={s.label} state={state} />
            {i < PRIMARY_STAGES.length - 1 && <Connector passed={passed} />}
          </div>
        );
      })}

      {/* Vertical divider before alternate terminal state */}
      <div
        data-divider="alt-terminal"
        className="self-stretch w-px mx-2"
        style={{ backgroundColor: "var(--color-border)" }}
        aria-hidden="true"
      />

      <StageNode label={ALT_TERMINAL.label} state={altTerminalState(current)} />
    </div>
  );
}
